/**
 * Daily life of the townsfolk (lane C0-NPC; shared data/npcLife.ts): the key poses of the life acts that the
 * townsfolk work poses (render/motions/folk.ts) and the emotes do not already have — eating, planting and harvesting,
 * casting a net, washing at the steps, the wok, chess and the zither, incense, the children's games, the martial
 * drills; the seated ones (`lifeSit*`) sit cross-legged like `sit`. Every pose is a rig layout, so it reads from all eight views and on men and women alike. Drawn on demand
 * through the emote channel (render/lifeArt.ts plays them).
 */
import { keyed, type PoseRigs } from './kit';

/** This group's poses. */
export type Poses =
  | 'lifeEatStand'
  | 'lifePlant'
  | 'lifeHarvest'
  | 'lifeCast'
  | 'lifeWash'
  | 'lifeHang'
  | 'lifeCook'
  | 'lifeServe'
  | 'lifeChop'
  | 'lifeIncense'
  | 'lifeSeek'
  | 'lifeHide'
  | 'lifeKick'
  | 'lifeSplash'
  | 'lifeStance'
  | 'lifeTeach'
  | 'lifeMimic'
  | 'lifeSitEat'
  | 'lifeSitNap'
  | 'lifeSitChess'
  | 'lifeSitZither'
  | 'lifeSitTell'
  | 'lifeSitMeditate';

/** The seated life poses (`lifeSit*`): drawn cross-legged on the ground like `sit` (render/sprites.ts `isSeated`). */
export const isLifeSeated = (pose: string): boolean => pose.startsWith('lifeSit');

export const POSES: PoseRigs<Poses> = {
  lifeEatStand: (ph) => {
    // Standing with a bun: held low (0), a bite (1).
    return ph
      ? { f1: -2, f2: 1, tilt: -3, arm: 'raise', far: 'hang' }
      : { f1: -2, f2: 1, tilt: 4, headDx: 1, arm: 'forward', far: 'hang' };
  },
  lifePlant: (ph) => {
    // Bent double in the paddy, a seedling pushed into the mud (1) and the next taken from the bundle (0).
    return ph
      ? { o: 3, lean: 1, tilt: 28, headDx: 2, f1: -6, f2: 5, arm: 'low', far: 'fwd', sway: 0.5 }
      : { o: 2, lean: 0, tilt: 24, headDx: 2, f1: -6, f2: 5, arm: 'lowBack', far: 'fwd', sway: -0.5 };
  },
  lifeHarvest: (ph) => {
    // Bent over the stalks: the far hand grips a bunch, the sickle hand draws back through (1).
    return ph
      ? { o: 2, lean: 1, tilt: 22, headDx: 1, f1: -6, f2: 5, arm: 'lowBack', far: 'fwd', sway: 1 }
      : { o: 2, lean: 1, tilt: 26, headDx: 2, f1: -6, f2: 5, arm: 'low', far: 'fwd', sway: -0.5 };
  },
  lifeCast: (ph) => {
    // Casting a net: gathered back (0), flung out wide (1), arms out as it falls (2), drawn in (3).
    return {
      f1: -6,
      f2: 5,
      ...keyed(
        [
          { o: 2, lean: -2, tilt: -8, arm: 'back', far: 'back', sway: 1 },
          { o: 0, lean: 2, tilt: 14, arm: 'out', far: 'up', sway: -1, face: 'atk' },
          { o: 1, lean: 1, tilt: 8, arm: 'forward', far: 'fwd', sway: 0 },
          { o: 3, lean: -1, tilt: 4, arm: 'low', far: 'fwd', sway: 0.5 },
        ],
        ph,
      ),
    };
  },
  lifeWash: (ph) => {
    // Crouched at the steps, scrubbing a cloth on the stone: pushed out (0), drawn back (1).
    return ph
      ? { o: 10, lean: 0, tilt: 22, headDx: 1, f1: -3, f2: 3, arm: 'low', far: 'fwd' }
      : { o: 10, lean: 1, tilt: 26, headDx: 2, f1: -3, f2: 3, arm: 'forward', far: 'fwd' };
  },
  lifeHang: (ph) => {
    // Shaking out a cloth (0) and hanging it high on the line (1).
    return ph
      ? { f1: -2, f2: 2, tilt: -10, headDx: -1, arm: 'overhead', far: 'up' }
      : { f1: -2, f2: 2, tilt: 4, arm: 'forward', far: 'fwd', sway: 1 };
  },
  lifeCook: (ph) => {
    // Tossing the wok: held level (0), flicked up (1).
    return ph
      ? { o: 1, lean: 1, tilt: 2, f1: -3, f2: 2, arm: 'raise', far: 'fwd', face: 'atk' }
      : { o: 2, lean: 1, tilt: 8, f1: -3, f2: 2, arm: 'forward', far: 'fwd' };
  },
  lifeServe: (ph) => {
    // A tray held out in both hands (0), set down with a little bow (1).
    return ph
      ? { o: 2, lean: 1, tilt: 16, headDx: 1, f1: -2, f2: 2, arm: 'low', far: 'fwd' }
      : { f1: -2, f2: 2, tilt: 2, arm: 'forward', far: 'fwd' };
  },
  lifeChop: (ph) => {
    // Swung up over the shoulder (0) and down into the trunk (1).
    return ph
      ? { o: 4, lean: 2, tilt: 24, headDx: 1, f1: -6, f2: 5, arm: 'low', far: 'fwd', sway: 1 }
      : { o: 0, lean: -1, tilt: -10, f1: -6, f2: 5, arm: 'overhead', far: 'up', sway: -0.5 };
  },
  lifeIncense: (ph) => {
    // Incense held up in both hands before the shrine (0), bowed over (1).
    return ph
      ? { o: 2, lean: 1, tilt: 24, headDx: 1, f1: -1, f2: 1, arm: 'clasp', far: 'clasp', closed: true }
      : { f1: -1, f2: 1, tilt: -2, arm: 'raise', far: 'clasp', closed: true };
  },
  lifeSeek: (ph) => {
    // Counting with the eyes covered, leaning forward; a peek (1).
    return { f1: -2, f2: 1, tilt: 14, headDx: 1, arm: 'face', far: 'face', closed: !ph };
  },
  lifeHide: (ph) => {
    // Crouched small, giggling (1: a little bob).
    return { o: 11 + (ph ? 1 : 0), lean: -1, tilt: 18, headDx: -1, f1: -3, f2: 3, arm: 'guard', far: 'clasp', face: ph ? 'cast' : 'idle' };
  },
  lifeKick: (ph) => {
    // Keeping a shuttlecock up: standing ready (0), the inside of the foot kicked up (1).
    return ph
      ? { o: 1, lean: -1, tilt: -4, f1: -2, f2: 5, l2: 6, arm: 'out', far: 'back', face: 'atk' }
      : { o: 2, tilt: 6, headDx: 1, f1: -3, f2: 2, arm: 'hang', far: 'hang' };
  },
  lifeSplash: (ph) => {
    // In the shallows: hands down in the water (0), a splash thrown up (1).
    return ph
      ? { o: 2, lean: -1, tilt: -6, f1: -4, f2: 3, arm: 'raise', far: 'up', face: 'cast' }
      : { o: 8, lean: 1, tilt: 20, headDx: 1, f1: -4, f2: 3, arm: 'low', far: 'fwd' };
  },
  lifeStance: (ph) => {
    // The horse stance (馬步): feet wide, low, fists out level; a breath (1 a touch lower).
    return { o: 8 + (ph ? 1 : 0), f1: -7, f2: 7, tilt: 0, arm: 'forward', far: 'fwd', face: 'atk' };
  },
  lifeTeach: (ph) => {
    // The teacher: arms folded watching (0), a hand pointing out the fault (1).
    return ph
      ? { o: 1, lean: 1, tilt: 6, f1: -4, f2: 3, arm: 'thrust', far: 'hip', face: 'atk' }
      : { f1: -3, f2: 3, lean: -1, arm: 'fold', far: 'fold' };
  },
  lifeMimic: (ph) => {
    // A child copying the martial with an open hand: drawn back (0), struck out with a shout (1).
    return ph
      ? { o: 2, lean: 2, tilt: 10, f1: -6, f2: 5, arm: 'thrust', far: 'back', face: 'atk' }
      : { o: 3, lean: -1, tilt: -4, f1: -6, f2: 5, arm: 'guard', far: 'fwd' };
  },
  lifeSitEat: (ph) => {
    // Sitting with a bowl held before the chest (0), chopsticks up to the mouth (1).
    return ph
      ? { o: 12, tilt: -4, arm: 'raise', far: 'fwd' }
      : { o: 12, tilt: 8, headDx: 1, arm: 'forward', far: 'fwd' };
  },
  lifeSitNap: (ph) => {
    // Dozing where they sat: head dropped, a slow breath (1 a little lower).
    return { o: 12 + (ph ? 1 : 0), tilt: 22, headDx: 1, arm: 'sit', far: 'fwd', closed: true };
  },
  lifeSitChess: (ph) => {
    // Sitting over the board: chin in hand (0), a piece moved (1).
    return ph
      ? { o: 12, lean: 2, tilt: 18, headDx: 1, arm: 'forward', far: 'fwd' }
      : { o: 12, tilt: 12, headDx: 1, arm: 'chin', far: 'fold' };
  },
  lifeSitZither: (ph) => {
    // The zither across the knees, both hands on the strings: plucked near (0), far (1).
    return ph
      ? { o: 12, lean: 1, tilt: 12, headDx: 1, arm: 'low', far: 'fwd' }
      : { o: 12, lean: 1, tilt: 10, headDx: 1, arm: 'forward', far: 'fwd', closed: true };
  },
  lifeSitTell: (ph) => {
    // Telling a tale among the children with the hands: one raised (0), both opened out (1).
    return ph
      ? { o: 12, lean: 1, tilt: 4, arm: 'forward', far: 'fwd', face: 'cast' }
      : { o: 12, tilt: -2, arm: 'raise', far: 'hang', face: 'cast' };
  },
  lifeSitMeditate: (ph) => {
    // Cross-legged, hands together before the navel, eyes shut; a slow breath (1).
    return { o: 12 + (ph ? 1 : 0), tilt: ph ? 2 : 0, arm: 'clasp', far: 'clasp', closed: true };
  },
};
